import { useCallback, useEffect, useState } from 'react'
import { listerPaiements, listerRemboursements, marquerRembourse } from '../../api/dispatch'
import type { Paiement, Remboursement } from '../../api/types'
import { fcfa } from '../Format'
import { Compteur } from '../Compteur'
import { ListeDeroulante } from '../ListeDeroulante'

/**
 * Paiements — TRANSPOSITION du gabarit d'origine.
 *
 * Manquaient : le filtre par prestataire, trois colonnes du tableau des
 * remboursements (payé, à rembourser, annulée par), le compteur d'en-tête et
 * l'explication du calcul. Ce n'est pas cosmétique : sans « Payé » à côté de
 * « À rembourser », rien ne montre qu'un remboursement est PARTIEL — et il
 * l'est chaque fois que le client est à l'origine de l'annulation.
 */
const ETIQUETTE_PAIEMENT: Record<string, [string, string]> = {
  succeeded: ['green', 'Reussi'],
  pending: ['yellow', 'En attente'],
  failed: ['red', 'Echoue'],
  refunded: ['blue', 'Rembourse'],
}

const PAR: Record<string, string> = {
  client: 'Client',
  merchant: 'Marchand',
  dispatch: 'Dispatch',
}

const MOTIF: Record<string, string> = {
  payment_failed: 'Paiement échoué',
  merchant_closed: 'Établissement fermé',
  out_of_stock: 'Rupture',
  client_request: 'Demande client',
  no_driver: 'Aucun livreur',
}

export function EcranPaiements() {
  const [paiements, setPaiements] = useState<Paiement[]>([])
  const [remboursements, setRemboursements] = useState<Remboursement[]>([])
  const [statut, setStatut] = useState('')
  const [fournisseur, setFournisseur] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)

  const charger = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([listerPaiements(), listerRemboursements()])
      setPaiements(p)
      setRemboursements(r)
      setErreur(null)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Lecture impossible.')
    }
  }, [])

  useEffect(() => {
    void charger()
  }, [charger])

  async function marquer(r: Remboursement) {
    if (!r.paiementId) return
    /* Cette console ne déclenche AUCUN virement : elle enregistre seulement
       qu'il a été fait à la main chez le prestataire. Marquer sans avoir viré
       laisse un client sans son argent, et la trace dit le contraire. */
    if (
      !window.confirm(
        'Confirmez-vous avoir effectué le remboursement chez le prestataire ?\n\n' +
          "Cette action ne déclenche AUCUN virement : elle enregistre seulement que vous l'avez fait.",
      )
    )
      return
    try {
      await marquerRembourse(r.paiementId)
      await charger()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
    }
  }

  const visibles = paiements.filter(
    (p) => (!statut || p.statut === statut) && (!fournisseur || p.fournisseur === fournisseur),
  )
  const total = paiements.reduce((s, p) => s + (p.statut === 'succeeded' ? p.montant : 0), 0)
  const attente = paiements.filter((p) => p.statut === 'pending').reduce((s, p) => s + p.montant, 0)
  const echoues = paiements.filter((p) => p.statut === 'failed').reduce((s, p) => s + p.montant, 0)

  const dus = remboursements.filter((r) => !r.fait)
  const montantDu = dus.reduce((s, r) => s + r.montant, 0)

  return (
    <>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body" style={{ fontSize: 12.5, color: 'var(--n700)', lineHeight: 1.5 }}>
          <b>Encaissements clients</b> — l'argent qui ENTRE : chaque commande payée par le client via
          Wave / Orange Money. C'est le suivi/rapprochement de ce que la plateforme a encaissé (à ne
          pas confondre avec « Paie livreurs » et « Paie marchands » = l'argent qui SORT vers les
          livreurs et les marchands).
        </div>
      </div>

      <div className="stats">
        <div className="stat">
          <div className="label">Total encaisse</div>
          <div className="val" style={{ color: 'var(--green)' }}>
            <Compteur valeur={fcfa(total)} />
          </div>
        </div>
        <div className="stat">
          <div className="label">En attente</div>
          <div className="val" style={{ color: 'var(--warning)' }}>
            <Compteur valeur={fcfa(attente)} />
          </div>
        </div>
        <div className="stat">
          <div className="label">Echoues</div>
          <div className="val" style={{ color: 'var(--danger)' }}>
            <Compteur valeur={fcfa(echoues)} />
          </div>
        </div>
      </div>

      {erreur && <p style={{ color: 'var(--danger)', fontSize: 13 }}>{erreur}</p>}

      {/* DEUX filtres. Sans celui du prestataire, rapprocher un relevé Wave
          impose de trier des centaines de lignes à l'œil. */}
      <div className="search-bar">
        <ListeDeroulante
          valeur={statut}
          surChangement={setStatut}
          options={[
            { valeur: '', libelle: 'Tous statuts' },
            { valeur: 'succeeded', libelle: 'Reussi' },
            { valeur: 'pending', libelle: 'En attente' },
            { valeur: 'failed', libelle: 'Echoue' },
          ]}
        />
        <ListeDeroulante
          valeur={fournisseur}
          surChangement={setFournisseur}
          options={[
            { valeur: '', libelle: 'Tous providers' },
            { valeur: 'wave', libelle: 'Wave' },
            { valeur: 'om', libelle: 'Orange Money' },
          ]}
        />
      </div>

      <div className="card">
        <div className="card-body">
          <table>
            <thead>
              <tr>
                <th>Ref</th>
                <th>Commande</th>
                <th>Client</th>
                <th>Provider</th>
                <th>Montant</th>
                <th>Statut</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((p) => {
                const [couleur, libelle] = ETIQUETTE_PAIEMENT[p.statut] ?? ['yellow', p.statut]
                return (
                  <tr key={p.id}>
                    <td>{p.ref}</td>
                    <td>{p.refCommande}</td>
                    <td>{p.clientNom}</td>
                    <td>{p.fournisseur === 'wave' ? 'Wave' : 'Orange Money'}</td>
                    <td>{fcfa(p.montant)}</td>
                    <td>
                      <span className={`badge badge-${couleur}`}>{libelle}</span>
                    </td>
                    <td>{new Date(p.creeLe).toLocaleDateString('fr-ML')}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header">
          Remboursements dus{' '}
          <span style={{ fontWeight: 400, color: 'var(--n400)', fontSize: 12 }}>
            {remboursements.length
              ? `· ${dus.length} en attente · ${fcfa(montantDu)} à rembourser`
              : '· aucun'}
          </span>
        </div>
        <div className="card-body">
          <div style={{ fontSize: 12.5, color: 'var(--n400)', lineHeight: 1.6, marginBottom: 12 }}>
            Commandes annulées après paiement. Le montant est calculé à l'annulation (les frais de
            service ne sont pas remboursés si c'est le client qui annule ; remboursement total si
            c'est le marchand ou nous). <strong>Aucun remboursement n'est automatique</strong> :
            effectuez le virement depuis Wave ou Orange Money, puis marquez-le ici.
          </div>
          <table>
            <thead>
              <tr>
                <th>Commande</th>
                <th>Client</th>
                {/* « Payé » à côté de « À rembourser » : c'est le seul endroit
                    qui montre qu'un remboursement est PARTIEL. */}
                <th>Payé</th>
                <th>À rembourser</th>
                <th>Annulée par</th>
                <th>Motif</th>
                <th>Provider</th>
                <th>Statut</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {remboursements.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ color: 'var(--n400)', padding: 18, textAlign: 'center' }}>
                    Aucun remboursement dû.
                  </td>
                </tr>
              )}
              {remboursements.map((r) => (
                <tr key={r.commandeId}>
                  <td>{r.ref ?? '—'}</td>
                  <td>{r.client}</td>
                  <td>{fcfa(r.total)}</td>
                  <td>
                    <strong>{fcfa(r.montant)}</strong>
                  </td>
                  <td>{PAR[r.annulePar ?? ''] ?? r.annulePar ?? '—'}</td>
                  <td>{MOTIF[r.motif ?? ''] ?? r.motif ?? '—'}</td>
                  <td>{r.fournisseur}</td>
                  <td>
                    {r.fait ? (
                      <span className="badge badge-blue">Effectué</span>
                    ) : (
                      <span className="badge badge-yellow">À rembourser</span>
                    )}
                  </td>
                  <td>
                    {r.fait ? (
                      <span className="badge badge-blue">Remboursé</span>
                    ) : r.payable ? (
                      <button className="btn btn-outline btn-sm" onClick={() => void marquer(r)}>
                        Marquer remboursé
                      </button>
                    ) : (
                      <span style={{ fontSize: 11.5, color: 'var(--n400)' }}>
                        Aucun paiement encaissé
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
